import { useEffect, useMemo, useRef, useState } from 'react';
import { AppSelect } from '@/components/shared/AppSelect';
import { Button } from '@/components/shared/Button';
import { Panel } from '@/components/shared/Panel';
import { WeaponIcon } from '@/components/shared/WeaponIcon';
import { WeaponSelect } from '@/components/shared/WeaponSelect';
import { roleCategoryOptions } from '@/data/options';
import { canMoveSlotWithinRoleGroup } from '@/features/events/builder';
import type { EventBuilderPartyDraft, EventBuilderSlotDraft, RoleCategory, WeaponCatalogItem } from '@/types/domain';

interface PartyBuilderProps {
  party: EventBuilderPartyDraft;
  weapons: WeaponCatalogItem[];
  joinedCountBySlotId: Record<string, number>;
  onLabelChange: (label: string) => void;
  onAddSlot: () => void;
  onRemoveSlot: (clientId: string) => void;
  onMoveSlot: (clientId: string, direction: 'up' | 'down') => void;
  onChangeSlot: (clientId: string, patch: Partial<EventBuilderSlotDraft>) => void;
}

function buildRoleSummary(party: EventBuilderPartyDraft, joinedCountBySlotId: Record<string, number>) {
  const roleMap = new Map<RoleCategory, { required: number; joined: number }>(
    roleCategoryOptions.map((roleCategory) => [roleCategory, { required: 0, joined: 0 }]),
  );

  for (const slot of party.slots.filter((entry) => entry.isActive)) {
    const existing = roleMap.get(slot.roleCategory) ?? { required: 0, joined: 0 };
    existing.required += slot.capacity;
    existing.joined += slot.id ? joinedCountBySlotId[slot.id] ?? 0 : 0;
    roleMap.set(slot.roleCategory, existing);
  }

  return roleCategoryOptions.map((roleCategory) => ({
    roleCategory,
    required: roleMap.get(roleCategory)?.required ?? 0,
    joined: roleMap.get(roleCategory)?.joined ?? 0,
  }));
}

export function PartyBuilder({
  party,
  weapons,
  joinedCountBySlotId,
  onLabelChange,
  onAddSlot,
  onRemoveSlot,
  onMoveSlot,
  onChangeSlot,
}: PartyBuilderProps): JSX.Element {
  const slotRefs = useRef<Record<string, HTMLElement | null>>({});
  const [highlightedSlotId, setHighlightedSlotId] = useState<string | null>(null);
  const [roleJumpHint, setRoleJumpHint] = useState<string | null>(null);
  const configuredSlotCount = party.slots.length;
  const activeSlots = party.slots.filter((slot) => slot.isActive);
  const activeSlotCount = activeSlots.length;
  const totalCapacity = activeSlots.reduce((sum, slot) => sum + slot.capacity, 0);
  const totalJoined = activeSlots.reduce((sum, slot) => sum + (slot.id ? joinedCountBySlotId[slot.id] ?? 0 : 0), 0);
  const remaining = Math.max(totalCapacity - totalJoined, 0);
  const roleSummary = buildRoleSummary(party, joinedCountBySlotId);
  const firstSlotByRole = useMemo(() => {
    const nextMap = new Map<RoleCategory, string>();

    for (const slot of party.slots) {
      if (!nextMap.has(slot.roleCategory)) {
        nextMap.set(slot.roleCategory, slot.clientId);
      }
    }

    return nextMap;
  }, [party.slots]);

  useEffect(() => {
    if (!highlightedSlotId) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      setHighlightedSlotId(null);
    }, 1800);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [highlightedSlotId]);

  useEffect(() => {
    if (!roleJumpHint) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      setRoleJumpHint(null);
    }, 2200);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [roleJumpHint]);

  const jumpToRole = (roleCategory: RoleCategory) => {
    const targetSlotId = firstSlotByRole.get(roleCategory);

    if (!targetSlotId) {
      setRoleJumpHint(`No ${roleCategory} slots in ${party.label} yet.`);
      return;
    }

    const targetElement = slotRefs.current[targetSlotId];

    if (!targetElement) {
      setRoleJumpHint(`Unable to find the ${roleCategory} section in ${party.label} right now.`);
      return;
    }

    setRoleJumpHint(null);
    setHighlightedSlotId(targetSlotId);
    targetElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return (
    <Panel
      title={party.label}
      description={`Party ${party.partyNumber}: ${configuredSlotCount} configured slots, ${activeSlotCount} active slots, ${totalJoined} / ${totalCapacity} joined, ${remaining} remaining.`}
      action={
        <Button variant="secondary" onClick={onAddSlot}>
          Add Slot
        </Button>
      }
    >
      <div className="party-builder__meta">
        <label className="field">
          <span className="field__label">Party Label</span>
          <input className="field__input" value={party.label} onChange={(event) => onLabelChange(event.target.value)} />
        </label>

        <div className="party-summary">
          <strong>
            {totalJoined} / {totalCapacity}
          </strong>
          <span>{remaining} remaining</span>
          <span>{configuredSlotCount} configured slots - {activeSlotCount} active</span>
        </div>
      </div>

      {roleSummary.length > 0 ? (
        <div className="party-role-summary">
          {roleSummary.map((entry) => (
            <button
              key={entry.roleCategory}
              type="button"
              className={`party-role-summary__item party-role-summary__item--interactive${
                entry.required <= 0 ? ' party-role-summary__item--empty' : ''
              }`}
              onClick={() => jumpToRole(entry.roleCategory)}
              aria-label={`Jump to ${entry.roleCategory} slots in ${party.label}`}
            >
              <strong>{entry.roleCategory}</strong>
              <span>
                {entry.joined} / {entry.required}
              </span>
            </button>
          ))}
        </div>
      ) : null}

      {roleJumpHint ? <p className="party-role-summary__hint" role="status">{roleJumpHint}</p> : null}

      <div className="builder-slot-stack">
        {party.slots.map((slot, index) => {
          const selectableWeapons = weapons.filter((weapon) => weapon.isActive || weapon.id === slot.weaponId);
          const canMoveUp = canMoveSlotWithinRoleGroup(party.slots, slot.clientId, 'up');
          const canMoveDown = canMoveSlotWithinRoleGroup(party.slots, slot.clientId, 'down');

          return (
            <article
              key={slot.clientId}
              ref={(element) => {
                slotRefs.current[slot.clientId] = element;
              }}
              className={`builder-slot-card${highlightedSlotId === slot.clientId ? ' builder-slot-card--highlighted' : ''}`}
            >
              <div className="builder-slot-card__header">
                <div>
                  <span className="builder-slot-card__eyebrow">Slot {index + 1}</span>
                  <div className="builder-slot-card__title">
                    <WeaponIcon imagePath={slot.imagePath} label={slot.weaponName || 'Weapon slot'} size="sm" />
                    <h4>{slot.weaponName || 'New slot'}</h4>
                  </div>
                  {slot.note ? <p className="builder-slot-card__note">{slot.note}</p> : null}
                </div>
                <div className="builder-slot-card__actions">
                  <Button variant="ghost" onClick={() => onMoveSlot(slot.clientId, 'up')} disabled={!canMoveUp}>
                    Move Up
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => onMoveSlot(slot.clientId, 'down')}
                    disabled={!canMoveDown}
                  >
                    Move Down
                  </Button>
                  <Button variant="danger" onClick={() => onRemoveSlot(slot.clientId)}>
                    Remove
                  </Button>
                </div>
              </div>

              <div className="form-grid form-grid--three">
                <label className="field">
                  <span className="field__label">Note</span>
                  <input
                    className="field__input"
                    value={slot.note}
                    onChange={(event) => onChangeSlot(slot.clientId, { note: event.target.value })}
                    placeholder="Optional officer note"
                  />
                </label>

                <label className="field">
                  <span className="field__label">Role Category</span>
                  <AppSelect
                    ariaLabel={`Role category for ${slot.weaponName || `slot ${index + 1}`}`}
                    value={slot.roleCategory}
                    options={roleCategoryOptions.map((category) => ({
                      value: category,
                      label: category,
                      description: 'Slot role category',
                    }))}
                    onChange={(value) => onChangeSlot(slot.clientId, { roleCategory: value as EventBuilderSlotDraft['roleCategory'] })}
                  />
                </label>

                <label className="field">
                  <span className="field__label">Weapon</span>
                  <WeaponSelect
                    value={slot.weaponId}
                    weapons={selectableWeapons}
                    onSelect={(weapon) => {
                      onChangeSlot(slot.clientId, {
                        weaponId: weapon?.id,
                        weaponName: weapon?.displayName ?? '',
                        imagePath: weapon?.imagePath ?? '',
                        roleCategory: weapon?.roleCategory ?? slot.roleCategory,
                      });
                    }}
                  />
                </label>
              </div>

              <div className="form-grid form-grid--three">
                <label className="field">
                  <span className="field__label">Capacity</span>
                  <input
                    className="field__input"
                    type="number"
                    min={1}
                    value={slot.capacity}
                    onChange={(event) =>
                      onChangeSlot(slot.clientId, { capacity: Math.max(Number.parseInt(event.target.value || '1', 10), 1) })
                    }
                  />
                </label>

                <div className="field field--checkbox">
                  <span className="field__label">Waitlist</span>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={slot.waitlistEnabled}
                      onChange={(event) => onChangeSlot(slot.clientId, { waitlistEnabled: event.target.checked })}
                    />
                    <span>{slot.waitlistEnabled ? 'Enabled' : 'Disabled'}</span>
                  </label>
                </div>

                <div className="field field--checkbox">
                  <span className="field__label">Slot State</span>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={slot.isActive}
                      onChange={(event) => onChangeSlot(slot.clientId, { isActive: event.target.checked })}
                    />
                    <span>{slot.isActive ? 'Active' : 'Inactive'}</span>
                  </label>
                </div>
              </div>

              {slot.imagePath ? (
                <div className="builder-slot-card__preview">
                  <WeaponIcon imagePath={slot.imagePath} label={slot.weaponName || 'Weapon slot'} size="md" />
                  <div>
                    <strong>{slot.weaponName || 'Weapon preview'}</strong>
                    <span>
                      {slot.roleCategory}
                      {slot.isActive ? '' : ' - inactive'}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="form-footnote">Choose a weapon to attach its icon automatically.</p>
              )}
            </article>
          );
        })}
      </div>
    </Panel>
  );
}
